import { Environment, resolveEnvironmentConfig, toEnvironment } from '../domain/types/Environment';
export const environment = import.meta.env.VITE_ENVIRONMENT;

// The single normalized (trimmed, case-insensitive) resolution of `environment` — use this
// for any Environment.* comparison instead of comparing the raw `environment` string
// directly, so whitespace-touched-but-otherwise-valid values behave consistently across
// config resolution (getEnvironmentConfig) and dev-only feature checks alike.
export const currentEnvironment: Environment = toEnvironment(environment);

// Single source of truth for "is this a non-production environment where dev-only
// affordances (skip buttons, mock service worker) are allowed" — used by the dev-skip
// checks across the DK pages and mockServiceWorker's isMockingAvailable, instead of each
// call site re-deriving the same predicate from currentEnvironment.
export const isLocalOrDevelopmentEnvironment: boolean =
    currentEnvironment === Environment.Local || currentEnvironment === Environment.Development;

export interface IEnvironmentConfig {
    name: string;
    oAuthBaseUrl: string;
    proxyBaseUrl: string;
    apiKey: string;
    applyFormVersion?: string;
    surveyId: string;
    checkOngoing: boolean;
    withCoApplicant: boolean;
    withCoApplicantConsolidations: boolean;
    showPpiPage: boolean;
}

export interface ICommonConfig {
    maxIdleTime: number;
    logoutWarnTime: number;
}

export const appConfig: ICommonConfig = {
    maxIdleTime: 300,
    logoutWarnTime: 30,
};

const local: IEnvironmentConfig = {
    name: 'local',
    oAuthBaseUrl: 'https://api.d.eks.aws.cld.resurs.com/api/cl_loanapp_proxy_service/auth',
    proxyBaseUrl: 'https://api.d.eks.aws.cld.resurs.com/api/cl_loanapp_proxy_service/v1/{countryCode}',
    apiKey: import.meta.env.VITE_API_KEY,
    surveyId: 'a37eb1a5Z6ec2',
    checkOngoing: true,
    withCoApplicant: true,
    withCoApplicantConsolidations: true,
    showPpiPage: true,
};

const development: IEnvironmentConfig = {
    name: 'dev',
    // Hard code endpoint for auth to openshift until it is fixed in AWS
    oAuthBaseUrl: '${gwservice}/api/cl_loanapp_proxy_service/auth',
    proxyBaseUrl: '${gwservice}/api/cl_loanapp_proxy_service/v1/{countryCode}',
    apiKey: import.meta.env.VITE_API_KEY,
    surveyId: 'a37eb1a5Z6ec2',
    checkOngoing: true,
    withCoApplicant: true,
    withCoApplicantConsolidations: true,
    showPpiPage: true,
};

const integration: IEnvironmentConfig = {
    name: 'integration',
    oAuthBaseUrl: '${gwservice}/api/cl_loanapp_proxy_service/auth',
    proxyBaseUrl: '${gwservice}/api/cl_loanapp_proxy_service/v1/{countryCode}',
    apiKey: import.meta.env.VITE_API_KEY,
    surveyId: 'a37eb1a5Z6ec2',
    checkOngoing: true,
    withCoApplicant: true,
    withCoApplicantConsolidations: true,
    showPpiPage: true,
};

const production: IEnvironmentConfig = {
    name: 'production',
    oAuthBaseUrl: '${gwservice}/api/cl_loanapp_proxy_service/auth',
    proxyBaseUrl: '${gwservice}/api/cl_loanapp_proxy_service/v1/{countryCode}',
    apiKey: import.meta.env.VITE_API_KEY,
    surveyId: 'a37eb1a5Z6ec2',
    checkOngoing: true,
    withCoApplicant: true,
    withCoApplicantConsolidations: true,
    showPpiPage: true,
};

export const getEnvironmentConfig = (): IEnvironmentConfig => {
    try {
        return resolveEnvironmentConfig(currentEnvironment, { development, integration, production, local });
    } catch (error) {
        throw new Error(`Unrecognised VITE_ENVIRONMENT: "${environment}"`, { cause: error });
    }
};
